import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { CHARACTERS } from "../assets/characters";

interface OnboardingData {
  firstName: string;
  lastName: string;
  jobTitle: string;
  jobTitleOther: string;
  rhLevel: string;
  objectives: string[];
  mainDifficulty: string;
  companyName: string;
  sector: string;
  activity: string;
  location: string;
  creationYear: string;
  headcountTotal: string;
  headcountCdi: string;
  headcountCdd: string;
  headcountAlternants: string;
  headcountStagiaires: string;
  headcountPartTime: string;
  headcountInterim: string;
  sitesCount: string;
  services: string;
  managers: string;
  ccnCode: string;
  ccnName: string;
  noCcn: boolean;
  legalStatus: string;
  legalStatusOther: string;
  obligations: Record<string, string>;
}

const JOB_TITLES = [
  "Dirigeant / Président",
  "Directeur général",
  "Gérant",
  "Membre du Conseil d’Administration",
  "Responsable administratif",
  "Responsable financier",
  "Manager",
  "Autre",
];

const RH_LEVELS = [
  {
    id: "debutant",
    label: "Débutant",
    text: "Je découvre les sujets RH et j’ai besoin d’être guidé.",
  },
  {
    id: "intermediaire",
    label: "Intermédiaire",
    text: "Je connais les bases mais je manque de temps ou de méthode.",
  },
  {
    id: "avance",
    label: "Avancé",
    text: "Je maîtrise les fondamentaux mais je veux structurer davantage.",
  },
  {
    id: "expert",
    label: "Expert",
    text: "Je souhaite surtout gagner du temps et piloter plus efficacement.",
  },
];

const OBJECTIVES = [
  "Je n’ai pas de RH dans mon entreprise",
  "Je veux sécuriser mes pratiques",
  "Je veux gagner du temps",
  "Je veux mieux gérer mes salariés",
  "Je veux structurer mon entreprise",
  "Je veux anticiper la croissance",
  "Je veux monter en compétences RH",
];

const CCN_OPTIONS = [
  { code: "1486", name: "Bureaux d’études techniques (Syntec)" },
  { code: "1516", name: "Organismes de formation" },
  { code: "1527", name: "Immobilier" },
  { code: "2120", name: "Banque" },
  {
    code: "2216",
    name: "Commerce de détail et de gros à prédominance alimentaire",
  },
  { code: "2596", name: "Coiffure" },
  { code: "3043", name: "Propreté" },
];

const LEGAL_STATUSES = ["SAS", "SARL", "Association", "Autre"];

const OBLIGATION_QUESTIONS: {
  key: string;
  label: string;
  options: string[];
}[] = [
  { key: "duerp", label: "DUERP ?", options: ["Oui", "Non", "Je ne sais pas"] },
  {
    key: "rup",
    label: "Registre unique du personnel ?",
    options: ["Oui", "Non"],
  },
  {
    key: "affichages",
    label: "Affichages obligatoires ?",
    options: ["Oui", "Non"],
  },
  { key: "cse", label: "CSE ?", options: ["Oui", "Non", "Non concerné"] },
  {
    key: "entretiens",
    label: "Entretiens professionnels réalisés ?",
    options: ["Oui", "Non"],
  },
  {
    key: "visites",
    label: "Visites médicales suivies ?",
    options: ["Oui", "Non"],
  },
];

const STORAGE_KEY = "julia_onboarding_v2";

const emptyData = (): OnboardingData => ({
  firstName: "",
  lastName: "",
  jobTitle: "",
  jobTitleOther: "",
  rhLevel: "",
  objectives: [],
  mainDifficulty: "",
  companyName: "",
  sector: "",
  activity: "",
  location: "",
  creationYear: "",
  headcountTotal: "",
  headcountCdi: "",
  headcountCdd: "",
  headcountAlternants: "",
  headcountStagiaires: "",
  headcountPartTime: "",
  headcountInterim: "",
  sitesCount: "",
  services: "",
  managers: "",
  ccnCode: "",
  ccnName: "",
  noCcn: false,
  legalStatus: "",
  legalStatusOther: "",
  obligations: {},
});

const fieldStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "0.9rem 1.05rem",
  borderRadius: "var(--radius-lg)",
  border: "1px solid var(--color-border)",
  fontFamily: "var(--font-family)",
  fontSize: "var(--font-size-sm)",
  lineHeight: 1.55,
  background: "#fff",
  color: "var(--color-text)",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  marginBottom: "0.35rem",
  fontSize: "var(--font-size-sm)",
  fontWeight: 600,
  color: "var(--color-text)",
};

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, markOnboardingComplete } = useAuth();
  const [step, setStep] = useState(0);
  const [data, setData] = useState<OnboardingData>(emptyData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as {
          step?: number;
          data?: OnboardingData;
        };
        if (parsed.data) setData({ ...emptyData(), ...parsed.data });
        if (typeof parsed.step === "number" && parsed.step >= 6) {
          // Reprise à partir de la compréhension entreprise (vue 6+)
          setStep(Math.max(6, parsed.step));
        } else if (typeof parsed.step === "number") {
          setStep(parsed.step);
        }
      } catch {
        /* ignore */
      }
    }
    if (user?.first_name || user?.last_name) {
      setData((prev) => ({
        ...prev,
        firstName: prev.firstName || user.first_name || "",
        lastName: prev.lastName || user.last_name || "",
      }));
    }
  }, [user]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, data }));
  }, [step, data]);

  const totalSteps = 14;

  const progress = ((step + 1) / totalSteps) * 100;

  const setField = <K extends keyof OnboardingData>(
    key: K,
    value: OnboardingData[K],
  ) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const toggleObjective = (item: string) => {
    setData((prev) => ({
      ...prev,
      objectives: prev.objectives.includes(item)
        ? prev.objectives.filter((o) => o !== item)
        : [...prev.objectives, item],
    }));
  };

  const effectifNumber = useMemo(() => {
    const n = parseInt(data.headcountTotal, 10);
    return Number.isFinite(n) ? n : 0;
  }, [data.headcountTotal]);

  const maturity = useMemo(() => {
    const levelMap: Record<string, number> = {
      debutant: 35,
      intermediaire: 55,
      avance: 72,
      expert: 85,
    };
    const base = levelMap[data.rhLevel] || 50;
    const answered = Object.values(data.obligations).filter(
      (v) => v === "Oui",
    ).length;
    const admin = Math.min(95, base + answered * 4);
    const conformite = Math.min(92, 40 + answered * 8 + (data.noCcn ? 0 : 10));
    const management = Math.min(88, base - 5 + (data.managers ? 10 : 0));
    const pilotage = Math.min(80, base - 15 + data.objectives.length * 3);
    const global = Math.round((admin + conformite + management + pilotage) / 4);
    return { admin, conformite, management, pilotage, global };
  }, [data]);

  const priorities = useMemo(() => {
    const list: {
      level: "urgent" | "soon" | "ok";
      title: string;
      detail: string;
    }[] = [];
    if (data.obligations.duerp !== "Oui") {
      list.push({
        level: "urgent",
        title: "Mettre à jour votre DUERP",
        detail: "Votre document unique n’est pas confirmé comme à jour.",
      });
    }
    if (data.obligations.entretiens !== "Oui") {
      list.push({
        level: "soon",
        title: "Structurer vos entretiens professionnels",
        detail: `Vous avez ${effectifNumber || "plusieurs"} salarié(s) potentiellement concernés.`,
      });
    }
    if (data.obligations.rup !== "Oui") {
      list.push({
        level: "soon",
        title: "Sécuriser le registre unique du personnel",
        detail: "Le registre est une obligation de base pour tout employeur.",
      });
    }
    if (list.length === 0) {
      list.push({
        level: "ok",
        title: "Consolider le pilotage RH",
        detail: "Passez aux priorités stratégiques et au suivi des échéances.",
      });
    }
    return list.slice(0, 3);
  }, [data.obligations, effectifNumber]);

  const canNext = (): boolean => {
    switch (step) {
      case 1:
        return Boolean(data.firstName.trim() && data.lastName.trim());
      case 2:
        return Boolean(
          data.jobTitle &&
          (data.jobTitle !== "Autre" || data.jobTitleOther.trim()),
        );
      case 3:
        return Boolean(data.rhLevel);
      case 4:
        return data.objectives.length > 0;
      case 5:
        return Boolean(data.mainDifficulty.trim());
      case 6:
        return Boolean(data.companyName.trim() && data.sector.trim());
      case 7:
        return Boolean(data.headcountTotal.trim());
      case 9:
        return data.noCcn || Boolean(data.ccnCode || data.ccnName);
      case 10:
        return Boolean(
          data.legalStatus &&
          (data.legalStatus !== "Autre" || data.legalStatusOther.trim()),
        );
      case 12:
        return OBLIGATION_QUESTIONS.every((q) => data.obligations[q.key]);
      default:
        return true;
    }
  };

  const finishOnboarding = async () => {
    setSaving(true);
    try {
      const effectif = effectifNumber || 0;
      await client.put("/structure/informations", {
        name: data.companyName || undefined,
        sector: data.sector || undefined,
        address: data.location || undefined,
        presentation: data.activity || undefined,
        effectif,
        dirigeants: [
          {
            nom: `${data.firstName} ${data.lastName}`.trim(),
            poste:
              data.jobTitle === "Autre" ? data.jobTitleOther : data.jobTitle,
          },
        ],
      });
      await client.put("/structure/gestion-personnel", {
        ccn_code: data.noCcn ? "" : data.ccnCode,
        ccn_nom: data.noCcn ? "" : data.ccnName,
        organigramme: {
          sites: data.sitesCount,
          services: data.services
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean),
          managers: data.managers
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean),
          effectifs: {
            cdi: data.headcountCdi,
            cdd: data.headcountCdd,
            alternants: data.headcountAlternants,
            stagiaires: data.headcountStagiaires,
            temps_partiels: data.headcountPartTime,
            interim: data.headcountInterim,
          },
          statut_juridique:
            data.legalStatus === "Autre"
              ? data.legalStatusOther
              : data.legalStatus,
          onboarding: {
            rh_level: data.rhLevel,
            objectives: data.objectives,
            main_difficulty: data.mainDifficulty,
            obligations: data.obligations,
            maturity,
          },
        },
      });
      await client.post("/structure/onboarding/complete", {});
      localStorage.removeItem(STORAGE_KEY);
      markOnboardingComplete();
      navigate("/dashboard");
    } catch {
      markOnboardingComplete();
      navigate("/dashboard");
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (step < totalSteps - 1) {
      setStep((s) => s + 1);
      return;
    }
    await finishOnboarding();
  };

  const handleBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  const downloadTemplate = () => {
    const headers = [
      "Nom de naissance",
      "Nom d’usage",
      "Prénom(s)",
      "Sexe",
      "Date de naissance",
      "Lieu de naissance",
      "Pays de naissance",
      "Nationalité",
      "Adresse personnelle",
      "Téléphone",
      "Email",
      "NIR",
      "Matricule",
      "Date d’entrée",
      "Date d’ancienneté",
      "Établissement",
      "Agence / site",
      "Service",
      "Manager",
      "Statut",
      "Type de contrat",
      "Date début contrat",
      "Date fin contrat",
      "Période d’essai",
      "Temps de travail",
      "Durée hebdomadaire",
      "Intitulé du poste",
      "Qualification",
      "Classification",
      "Coefficient / niveau",
      "Lieu de travail",
      "Télétravail",
    ];
    const csv = `${headers.join(";")}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "modele-registre-unique-personnel.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderChoiceCard = (
    selected: boolean,
    title: string,
    subtitle: string,
    onClick: () => void,
  ) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left",
        padding: "0.9rem 1rem",
        borderRadius: "var(--radius-xl)",
        border: selected
          ? "2px solid var(--color-brand)"
          : "1px solid var(--color-border)",
        background: selected ? "var(--color-brand-light)" : "#fff",
        cursor: "pointer",
        fontFamily: "var(--font-family)",
      }}
    >
      <div
        style={{
          fontWeight: 700,
          color: "var(--color-primary)",
          marginBottom: 4,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: "var(--font-size-sm)",
          color: "var(--color-text-secondary)",
        }}
      >
        {subtitle}
      </div>
    </button>
  );

  const script = (children: React.ReactNode) => (
    <p
      style={{
        margin: "0 0 1.25rem",
        lineHeight: 1.7,
        color: "var(--color-text)",
      }}
    >
      {children}
    </p>
  );

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <>
            {script(
              <>
                Bonjour, je suis Julia, votre nouvelle DRH 100% virtuelle.
                <br />
                <br />
                Tout d’abord, merci pour votre confiance. Vous rejoignez
                aujourd’hui les dirigeants qui font le choix d’une gestion RH
                plus simple, plus moderne et plus efficace.
                <br />
                <br />
                Ma mission est simple : vous aider à prendre les bonnes
                décisions RH, au bon moment, sans complexité inutile. Prêt(e) à
                commencer ? C’est parti !
              </>,
            )}
          </>
        );
      case 1:
        return (
          <>
            {script(
              <>
                Maintenant que vous me connaissez un peu mieux, j’aimerais en
                savoir davantage sur vous.
              </>,
            )}
            {script(<>Commençons par le début : comment vous appelez-vous ?</>)}
            <div style={{ display: "grid", gap: "0.85rem" }}>
              <div>
                <label style={labelStyle}>Prénom</label>
                <input
                  style={fieldStyle}
                  value={data.firstName}
                  onChange={(e) => setField("firstName", e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Nom</label>
                <input
                  style={fieldStyle}
                  value={data.lastName}
                  onChange={(e) => setField("lastName", e.target.value)}
                />
              </div>
            </div>
          </>
        );
      case 2:
        return (
          <>
            {script(
              <>
                Enchanté {data.firstName || "à vous"} ! Quel poste occupez-vous
                dans l’entreprise ?
              </>,
            )}
            <div style={{ display: "grid", gap: "0.55rem" }}>
              {JOB_TITLES.map((title) => (
                <label
                  key={title}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.55rem",
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  <input
                    type="radio"
                    name="job"
                    checked={data.jobTitle === title}
                    onChange={() => setField("jobTitle", title)}
                  />
                  {title}
                </label>
              ))}
            </div>
            {data.jobTitle === "Autre" && (
              <div style={{ marginTop: "0.85rem" }}>
                <label style={labelStyle}>Précisez votre poste</label>
                <input
                  style={fieldStyle}
                  value={data.jobTitleOther}
                  onChange={(e) => setField("jobTitleOther", e.target.value)}
                />
              </div>
            )}
          </>
        );
      case 3:
        return (
          <>
            {script(
              <>
                À présent, comment évaluez-vous votre niveau actuel en
                ressources humaines ?
              </>,
            )}
            <div style={{ display: "grid", gap: "0.65rem" }}>
              {RH_LEVELS.map((level) =>
                renderChoiceCard(
                  data.rhLevel === level.id,
                  level.label,
                  level.text,
                  () => setField("rhLevel", level.id),
                ),
              )}
            </div>
          </>
        );
      case 4:
        return (
          <>
            {script(<>Pourquoi avez-vous choisi Julia aujourd’hui ?</>)}
            <div style={{ display: "grid", gap: "0.5rem" }}>
              {OBJECTIVES.map((item) => (
                <label
                  key={item}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "0.55rem",
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={data.objectives.includes(item)}
                    onChange={() => toggleObjective(item)}
                    style={{ marginTop: 3 }}
                  />
                  {item}
                </label>
              ))}
            </div>
          </>
        );
      case 5:
        return (
          <>
            {script(
              <>
                Si vous pouviez résoudre un seul problème RH aujourd’hui, lequel
                serait-ce ?
              </>,
            )}
            <textarea
              style={{
                ...fieldStyle,
                minHeight: 128,
                resize: "vertical",
                padding: "1rem 1.15rem",
              }}
              value={data.mainDifficulty}
              onChange={(e) => setField("mainDifficulty", e.target.value)}
              placeholder="Décrivez votre principale difficulté…"
            />
          </>
        );
      case 6:
        return (
          <>
            {script(
              <>
                Je vous remercie pour ces premières informations. Découvrons
                maintenant votre entreprise.
              </>,
            )}
            <div style={{ display: "grid", gap: "0.75rem" }}>
              {[
                ["companyName", "Nom de l’entreprise"],
                ["sector", "Secteur d’activité"],
                ["activity", "Activité principale"],
                ["location", "Localisation"],
                ["creationYear", "Année de création"],
              ].map(([key, label]) => (
                <div key={key}>
                  <label style={labelStyle}>{label}</label>
                  <input
                    style={fieldStyle}
                    value={data[key as keyof OnboardingData] as string}
                    onChange={(e) =>
                      setField(key as keyof OnboardingData, e.target.value)
                    }
                  />
                </div>
              ))}
            </div>
          </>
        );
      case 7:
        return (
          <>
            {script(<>Combien de salariés avez-vous aujourd’hui ?</>)}
            <div
              style={{
                display: "grid",
                gap: "0.75rem",
                gridTemplateColumns: "1fr 1fr",
              }}
            >
              {[
                ["headcountTotal", "Effectif total"],
                ["headcountCdi", "CDI"],
                ["headcountCdd", "CDD"],
                ["headcountAlternants", "Alternants"],
                ["headcountStagiaires", "Stagiaires"],
                ["headcountPartTime", "Temps partiels"],
                ["headcountInterim", "Prestataires / intérim"],
              ].map(([key, label]) => (
                <div
                  key={key}
                  style={
                    key === "headcountTotal"
                      ? { gridColumn: "1 / -1" }
                      : undefined
                  }
                >
                  <label style={labelStyle}>{label}</label>
                  <input
                    type="number"
                    min={0}
                    style={fieldStyle}
                    value={data[key as keyof OnboardingData] as string}
                    onChange={(e) =>
                      setField(key as keyof OnboardingData, e.target.value)
                    }
                  />
                </div>
              ))}
            </div>
          </>
        );
      case 8:
        return (
          <>
            {script(<>Comment votre entreprise est-elle organisée ?</>)}
            <div style={{ display: "grid", gap: "0.75rem" }}>
              <div>
                <label style={labelStyle}>Nombre de sites</label>
                <input
                  style={fieldStyle}
                  value={data.sitesCount}
                  onChange={(e) => setField("sitesCount", e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>
                  Liste des services (un par ligne)
                </label>
                <textarea
                  style={{ ...fieldStyle, minHeight: 90 }}
                  value={data.services}
                  onChange={(e) => setField("services", e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>
                  Liste des managers (un par ligne)
                </label>
                <textarea
                  style={{ ...fieldStyle, minHeight: 90 }}
                  value={data.managers}
                  onChange={(e) => setField("managers", e.target.value)}
                />
              </div>
            </div>
          </>
        );
      case 9:
        return (
          <>
            {script(<>Quelle convention collective appliquez-vous ?</>)}
            <div
              style={{
                display: "grid",
                gap: "0.5rem",
                marginBottom: "0.85rem",
              }}
            >
              {CCN_OPTIONS.map((ccn) => (
                <label
                  key={ccn.code}
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  <input
                    type="radio"
                    name="ccn"
                    checked={!data.noCcn && data.ccnCode === ccn.code}
                    onChange={() => {
                      setField("noCcn", false);
                      setField("ccnCode", ccn.code);
                      setField("ccnName", ccn.name);
                    }}
                  />
                  IDCC {ccn.code} : {ccn.name}
                </label>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                setField("noCcn", true);
                setField("ccnCode", "");
                setField("ccnName", "");
              }}
              style={{
                ...fieldStyle,
                cursor: "pointer",
                background: data.noCcn ? "var(--color-brand-light)" : "#fff",
                borderColor: data.noCcn
                  ? "var(--color-brand)"
                  : "var(--color-border)",
                fontWeight: 600,
              }}
            >
              Je n’ai pas de convention collective
            </button>
          </>
        );
      case 10:
        return (
          <>
            {script(<>Quel est le statut juridique de votre entreprise ?</>)}
            <div style={{ display: "grid", gap: "0.5rem" }}>
              {LEGAL_STATUSES.map((status) => (
                <label
                  key={status}
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  <input
                    type="radio"
                    name="legal"
                    checked={data.legalStatus === status}
                    onChange={() => setField("legalStatus", status)}
                  />
                  {status}
                </label>
              ))}
            </div>
            {data.legalStatus === "Autre" && (
              <div style={{ marginTop: "0.85rem" }}>
                <label style={labelStyle}>Précisez</label>
                <input
                  style={fieldStyle}
                  value={data.legalStatusOther}
                  onChange={(e) => setField("legalStatusOther", e.target.value)}
                />
              </div>
            )}
          </>
        );
      case 11:
        return (
          <>
            {script(
              <>
                Pour vous éviter une saisie manuelle, souhaitez-vous importer
                vos données salariés ?
              </>,
            )}
            <div style={{ display: "grid", gap: "0.85rem" }}>
              <button
                type="button"
                onClick={downloadTemplate}
                className="lp-btn"
                style={{
                  ...fieldStyle,
                  cursor: "pointer",
                  fontWeight: 600,
                  background: "var(--color-brand-light)",
                  borderColor: "var(--color-brand)",
                }}
              >
                Télécharger le modèle (registre unique du personnel)
              </button>
              <label
                style={{
                  ...fieldStyle,
                  minHeight: 110,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderStyle: "dashed",
                  cursor: "pointer",
                  color: "var(--color-text-secondary)",
                }}
              >
                Déposer un fichier Excel / CSV ici (optionnel)
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  style={{ display: "none" }}
                />
              </label>
              <p
                style={{
                  margin: 0,
                  fontSize: "var(--font-size-sm)",
                  color: "var(--color-text-muted)",
                }}
              >
                Vous pourrez aussi importer vos salariés plus tard depuis Mon
                équipe.
              </p>
            </div>
          </>
        );
      case 12:
        return (
          <>
            {script(<>Avez-vous déjà mis en place ces éléments essentiels ?</>)}
            <div style={{ display: "grid", gap: "1rem" }}>
              {OBLIGATION_QUESTIONS.map((q) => (
                <div key={q.key}>
                  <div style={{ ...labelStyle, marginBottom: "0.5rem" }}>
                    {q.label}
                  </div>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}
                  >
                    {q.options.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() =>
                          setField("obligations", {
                            ...data.obligations,
                            [q.key]: opt,
                          })
                        }
                        style={{
                          padding: "0.45rem 0.85rem",
                          borderRadius: "var(--radius-full)",
                          border:
                            data.obligations[q.key] === opt
                              ? "2px solid var(--color-brand)"
                              : "1px solid var(--color-border)",
                          background:
                            data.obligations[q.key] === opt
                              ? "var(--color-brand-light)"
                              : "#fff",
                          cursor: "pointer",
                          fontSize: "var(--font-size-sm)",
                          fontWeight: 600,
                          fontFamily: "var(--font-family)",
                        }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        );
      case 13:
        return (
          <>
            {script(<>Voici votre diagnostic RH initial.</>)}
            <div
              style={{
                background: "var(--color-bg)",
                borderRadius: "var(--radius-xl)",
                padding: "1rem 1.1rem",
                marginBottom: "1rem",
              }}
            >
              <strong>Votre entreprise</strong>
              <ul
                style={{
                  margin: "0.5rem 0 0",
                  paddingLeft: "1.1rem",
                  color: "var(--color-text-secondary)",
                  fontSize: "var(--font-size-sm)",
                }}
              >
                <li>{effectifNumber || "-"} salariés</li>
                <li>
                  Convention collective :{" "}
                  {data.noCcn ? "non applicable" : data.ccnName || "à préciser"}
                </li>
                <li>
                  Niveau RH :{" "}
                  {RH_LEVELS.find((l) => l.id === data.rhLevel)?.label || "-"}
                </li>
              </ul>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "0.55rem",
                marginBottom: "1rem",
              }}
            >
              {[
                ["Administration", maturity.admin],
                ["Conformité", maturity.conformite],
                ["Management", maturity.management],
                ["Pilotage stratégique", maturity.pilotage],
              ].map(([label, value]) => (
                <div
                  key={String(label)}
                  style={{
                    background: "#fff",
                    border: "1px solid var(--color-border)",
                    borderRadius: 10,
                    padding: "0.65rem 0.75rem",
                  }}
                >
                  <div
                    style={{ fontSize: 12, color: "var(--color-text-muted)" }}
                  >
                    {label}
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      color: "var(--color-brand-dark)",
                    }}
                  >
                    {value} %
                  </div>
                </div>
              ))}
            </div>
            <p style={{ margin: "0 0 0.75rem", fontWeight: 700 }}>
              Score global : {maturity.global} %
            </p>
            <p style={{ margin: "0 0 0.5rem", fontWeight: 700 }}>
              Mes 3 priorités pour vous :
            </p>
            <div style={{ display: "grid", gap: "0.55rem" }}>
              {priorities.map((p, i) => (
                <div
                  key={p.title}
                  style={{
                    borderLeft: `4px solid ${p.level === "urgent" ? "#E74C3C" : p.level === "soon" ? "#F39C12" : "#27AE60"}`,
                    background: "#fff",
                    borderRadius: 8,
                    padding: "0.65rem 0.8rem",
                  }}
                >
                  <div
                    style={{ fontWeight: 700, fontSize: "var(--font-size-sm)" }}
                  >
                    Priorité {i + 1} : {p.title}
                  </div>
                  <div
                    style={{
                      fontSize: "var(--font-size-sm)",
                      color: "var(--color-text-secondary)",
                    }}
                  >
                    {p.detail}
                  </div>
                </div>
              ))}
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const ctaLabel =
    step === 0
      ? "Lancer mon diagnostic"
      : step === totalSteps - 1
        ? saving
          ? "Enregistrement…"
          : "Accéder à mon tableau de bord"
        : "Suivant";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--color-bg)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          opacity: 0.4,
          pointerEvents: "none",
          display: "flex",
        }}
      >
        <div
          style={{
            width: 240,
            background: "#fff",
            borderRight: "1px solid var(--color-border-light)",
          }}
        />
        <div style={{ flex: 1, padding: "2rem" }}>
          <div
            style={{
              height: 120,
              background: "#fff",
              borderRadius: 12,
              marginBottom: 16,
            }}
          />
          <div style={{ height: 280, background: "#fff", borderRadius: 12 }} />
        </div>
      </div>

      <div
        style={{
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.5rem",
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            style={{
              width: "100%",
              maxWidth: 820,
              background: "var(--color-surface)",
              borderRadius: "var(--radius-2xl)",
              boxShadow: "0 24px 48px rgba(27, 42, 74, 0.12)",
              overflow: "hidden",
              display: "grid",
              gridTemplateColumns: "1fr 260px",
              minHeight: 440,
            }}
          >
            <div
              style={{
                padding: "1.75rem 1.75rem 1.35rem",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  height: 4,
                  background: "var(--color-border)",
                  borderRadius: 99,
                  marginBottom: "1.25rem",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${progress}%`,
                    height: "100%",
                    background: "var(--color-brand)",
                    transition: "width 0.25s ease",
                  }}
                />
              </div>

              <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
                {renderStep()}
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "1.25rem",
                  gap: "0.75rem",
                }}
              >
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={step === 0}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "var(--font-size-sm)",
                    color:
                      step === 0
                        ? "var(--color-text-muted)"
                        : "var(--color-text-secondary)",
                    cursor: step === 0 ? "default" : "pointer",
                    fontFamily: "var(--font-family)",
                  }}
                >
                  Retour
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!canNext() || saving}
                  style={{
                    padding: "0.7rem 1.5rem",
                    background:
                      canNext() && !saving
                        ? "var(--color-brand)"
                        : "var(--color-border)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "var(--radius-full)",
                    fontSize: "var(--font-size-sm)",
                    fontWeight: 600,
                    cursor: canNext() && !saving ? "pointer" : "not-allowed",
                    fontFamily: "var(--font-family)",
                    boxShadow: canNext()
                      ? "0 4px 14px rgba(14, 165, 233, 0.35)"
                      : "none",
                  }}
                >
                  {ctaLabel}
                </button>
              </div>
            </div>

            <div
              style={{
                background:
                  "linear-gradient(180deg, #E0F7FA 0%, #80DEEA 45%, #26C6DA 100%)",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                padding: "1.5rem 0.75rem 0",
                minHeight: "100%",
                overflow: "hidden",
              }}
            >
              <img
                src={`${CHARACTERS.onboarding}?v=diverse`}
                alt="Julia, accompagnement RH"
                style={{
                  width: "auto",
                  maxWidth: "92%",
                  height: "auto",
                  maxHeight: 320,
                  objectFit: "contain",
                  objectPosition: "center bottom",
                  display: "block",
                  margin: "0 auto",
                }}
              />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default OnboardingPage;
